import { Component, HostListener, OnDestroy, Optional } from '@angular/core';
import { from, Observable, ReplaySubject, Subject, zip } from 'rxjs';
import { map, shareReplay, switchMap, takeUntil, tap } from 'rxjs/operators';
import { ValidationErrorsService } from '../avano-validation/validation-errors-service';
import { AvanoUnsavedChangesSupport } from './avano-unsaved-changes.common';
import { AvanoUnsavedChangesService } from './avano-unsaved-changes.service';

// TODO: i18n in avano? primeNG-approach? With a dedicated service?
// Every application has a basic set of translation keys? "avano. ....."?
export interface DisplayedUnsavedFormContext {
  ref: AvanoUnsavedChangesSupport;
  saveSupported: boolean;
  canSave: boolean;
  formId: string;
  formDisplayNameKey?: string;
}
export const UnsavedFormContextMapper = {
  fromContext: (formContext: AvanoUnsavedChangesSupport) => {
    if (!formContext?.unsavedChangesConfiguration) return null;
    const unsavedChangesConfig = formContext.unsavedChangesConfiguration;
    const saveSupported = typeof unsavedChangesConfig.saveAction == 'function' &&  typeof unsavedChangesConfig.canSave === 'function';
    const canSave = saveSupported && unsavedChangesConfig.canSave()
    const formDisplayNameKey = typeof unsavedChangesConfig.getDisplayedComponentName === 'function' && unsavedChangesConfig.getDisplayedComponentName() || undefined;
    return {
      ref: formContext,
      saveSupported,
      canSave,
      formDisplayNameKey,
      formId: unsavedChangesConfig.componentIdentifier
    }
  } 
}
@Component({
  selector: 'avano-unsaved-changes-dialog',
  templateUrl: './avano-unsaved-changes.component.html',
  styleUrls: ['./avano-unsaved-changes.component.scss']
})
export class AvanoUnsavedChangesComponent implements OnDestroy {
  private destroyed$ = new Subject<void>();
  contextType: { $implicit: DisplayedUnsavedFormContext};

  applyToAll: boolean;
  validationErrors = [];
  unsavedForms$: Observable<AvanoUnsavedChangesSupport[]> = this.avanoUnsavedChangesService.allUnsavedComponents$.pipe(
    tap(() => {
      this._nextFormPrompt$ = new ReplaySubject(1);
      this._nextFormPrompt$.next();
    }),
    shareReplay(1)
  );

  _nextFormPrompt$: Subject<void>;
  currentFormPrompt$: Observable<DisplayedUnsavedFormContext> = this.unsavedForms$.pipe(
    switchMap(allForms => zip(from([...allForms, null]), this._nextFormPrompt$).pipe(map(([form]) => form))),
    map(UnsavedFormContextMapper.fromContext),
    shareReplay(1)
  );

  constructor(private avanoUnsavedChangesService: AvanoUnsavedChangesService, @Optional() private validationService: ValidationErrorsService) {
    validationService?.changed$.pipe(takeUntil(this.destroyed$)).subscribe({
      next: errors => {
        this.validationErrors = errors;
      }
    })
  }
  ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  discardChanges(ctxt: AvanoUnsavedChangesSupport): void {
    const currentStepper = this._nextFormPrompt$;
    this.avanoUnsavedChangesService.pushDialogResponse({buttonClicked: 'discard', context: ctxt, applyAllChecked: this.applyToAll })
    currentStepper.next();
  }

  resumeEditing(ctxt: AvanoUnsavedChangesSupport): void {
    this.avanoUnsavedChangesService.pushDialogResponse({buttonClicked: 'cancel', context: ctxt, applyAllChecked: this.applyToAll })
  }
  
  saveChanges(ctxt: AvanoUnsavedChangesSupport): void {
    const currentStepper = this._nextFormPrompt$;
    this.avanoUnsavedChangesService.pushDialogResponse({buttonClicked: 'save', context: ctxt, applyAllChecked: this.applyToAll })
    currentStepper.next();
  }

  @HostListener('window:beforeunload', ['$event'])
  private attachToBeforeUnloadEvent(originalEvent: any): void {
    if (this.avanoUnsavedChangesService.getDirtyComponents().length > 0) {
      originalEvent.preventDefault();
      // This does not need to be i18n, because the browsers do
      // not allow anymore to set the text of the on-unload-confirmdialog
      const confirmationMessage = 'Unsaved changes';
      originalEvent.returnValue = confirmationMessage;
    }
  }
}
